import { CHOICE_GROUP_NUMBERING, QUESTION_TYPES } from '../constants';

/*
 * Shared layout logic for the question-navigation trays — the one shown while a student is
 * attempting a paper, and the one shown while they review it afterwards.
 *
 * Both trays hold the same three kinds of thing: loose questions, "attempt any X of these Y"
 * choice groups, and reading-comprehension passages. The rule that matters is that they read
 * in paper order. Rendering them as three lists one after another - which both trays used to
 * do - puts Q5 above a group of Q2-Q4, because position then follows the kind of block rather
 * than the paper.
 *
 * The two trays draw the same rows very differently (one has bookmarks and skip states, the
 * other has right/wrong), so only the ordering and numbering live here.
 */

const asArray = (value) => (Array.isArray(value) ? value : []);

const isPassage = (question) =>
  question?.type === QUESTION_TYPES.READING_COMPREHENSION ||
  asArray(question?.subQuestions).length > 0;

/** Appends to the current run of loose questions, or starts a new one. */
const pushLoose = (rows, item) => {
  const last = rows[rows.length - 1];
  if (last?.type === 'questions') last.items.push(item);
  else rows.push({ type: 'questions', items: [item] });
};

/**
 * One section's contents in paper order. Each block is emitted where its first question sits,
 * and runs of consecutive loose questions are batched so they keep sharing one grid instead of
 * each becoming a full-width row.
 *
 * A rule's row carries its `alternatives` in paper order - `{ type: 'question', item }` or a
 * passage's whole `{ type: 'rc', parent, subQuestions }` block, since a rule offering a passage
 * names the passage, never its questions - and `members`, every question row inside them.
 *
 * `rows` are the tray's own per-question objects, in the order of the student's response rows
 * (shuffled on a randomised paper), each carrying a `questionId`.
 */
export const buildDrawerSectionRows = ({ section, rows, assignmentQuestions = [] }) => {
  const items = asArray(rows);

  const choiceGroups = asArray(section?.choiceGroups).filter((group) => !group.parentQuestionId);
  const groupByMember = new Map();
  choiceGroups.forEach((group) => {
    asArray(group.questions).forEach((id) => groupByMember.set(String(id), group));
  });

  const parentOf = (questionId) =>
    asArray(assignmentQuestions).find(
      (question) =>
        isPassage(question) &&
        asArray(question.subQuestions).some((id) => String(id) === String(questionId))
    ) || null;

  const passageIds = new Set(
    asArray(assignmentQuestions).filter(isPassage).map((question) => String(question.id))
  );

  // A passage's own row, where one exists, is covered by its block.
  const isPassageRow = (row) =>
    !parentOf(row.questionId) && passageIds.has(String(row.questionId));

  /** What a row stands for on the paper: its passage for one of a passage's questions. */
  const blockIdOf = (row) => {
    const parent = parentOf(row.questionId);
    return parent ? String(parent.id) : String(row.questionId);
  };

  const passageBlock = (parent) => ({
    type: 'rc',
    parent,
    subQuestions: items.filter((row) =>
      asArray(parent.subQuestions).some((subId) => String(subId) === String(row.questionId))
    ),
  });

  const alternativesOf = (group) => {
    const memberIds = new Set(asArray(group.questions).map(String));
    const seen = new Set();
    const alternatives = [];
    items.forEach((row) => {
      if (isPassageRow(row)) return;
      const blockId = blockIdOf(row);
      if (!memberIds.has(blockId) || seen.has(blockId)) return;
      seen.add(blockId);
      const parent = parentOf(row.questionId);
      alternatives.push(parent ? passageBlock(parent) : { type: 'question', item: row });
    });
    return alternatives;
  };

  const out = [];
  const emitted = new Set();

  items.forEach((item) => {
    if (isPassageRow(item)) return;

    const parent = parentOf(item.questionId);
    const group = groupByMember.get(blockIdOf(item));
    if (group) {
      const key = `group:${group.id}`;
      if (emitted.has(key)) return;
      emitted.add(key);
      const alternatives = alternativesOf(group);
      out.push({
        type: 'choiceGroup',
        group,
        alternatives,
        members: alternatives.flatMap((alternative) =>
          alternative.type === 'rc' ? alternative.subQuestions : [alternative.item]
        ),
      });
      return;
    }

    // A sub-question stands for its passage's whole block, drawn at the first one.
    if (parent) {
      const key = `rc:${parent.id}`;
      if (emitted.has(key)) return;
      emitted.add(key);
      out.push(passageBlock(parent));
      return;
    }

    pushLoose(out, item);
  });

  return out;
};

/**
 * A rule's alternatives as a tray lays them out: consecutive questions share one grid, and each
 * passage takes a full-width block of its own.
 */
export const batchDrawerAlternatives = (alternatives = []) => {
  const out = [];
  asArray(alternatives).forEach((alternative) => {
    if (alternative.type === 'rc') out.push(alternative);
    else pushLoose(out, alternative.item);
  });
  return out;
};

/** The question rows an alternative stands for: a passage's questions, or the one question. */
export const alternativeRows = (alternative) =>
  (alternative?.type === 'rc' ? asArray(alternative.subQuestions) : [alternative?.item].filter(Boolean));

/**
 * A passage's own questions in order, with any choice among them collected into a block where
 * its first question sits. A passage may hold more than one such choice.
 */
export const buildDrawerSubQuestionRows = ({ parent, rows, allChoiceGroups = [] }) => {
  const items = asArray(rows);
  const groups = asArray(allChoiceGroups).filter(
    (group) => String(group.parentQuestionId) === String(parent?.id)
  );

  const groupByMember = new Map();
  groups.forEach((group) => {
    asArray(group.questions).forEach((id) => groupByMember.set(String(id), group));
  });

  const out = [];
  const emitted = new Set();

  items.forEach((item) => {
    const group = groupByMember.get(String(item.questionId));
    if (!group) {
      pushLoose(out, item);
      return;
    }
    if (emitted.has(group.id)) return;
    emitted.add(group.id);
    out.push({
      type: 'choiceGroup',
      group,
      members: items.filter((row) =>
        asArray(group.questions).some((id) => String(id) === String(row.questionId))
      ),
    });
  });

  return out;
};

const GROUP_MEMBER_LETTERS = 'abcdefghijklmnopqrstuvwxyz';

/**
 * The paper as one student was dealt it: each section's questions, and each passage's, in the
 * order of that student's response rows, leaving out anything they have no row for.
 *
 * The rows are fixed when the student starts, and a paper that randomises its questions is
 * shuffled then, a rule's alternatives moving as one block the way a passage's questions do.
 * A passage has no row of its own, so it sits where its first question does.
 */
const orderByResponses = ({ sections, assignmentQuestions, responses }) => {
  const position = new Map();
  asArray(responses).forEach((row, index) => {
    const id = String(row?.questionId);
    if (!position.has(id)) position.set(id, index);
  });

  const byPosition = (ids) =>
    asArray(ids)
      .map(String)
      .filter((id) => position.has(id))
      .sort((a, b) => position.get(a) - position.get(b));

  const questions = asArray(assignmentQuestions).map((question) =>
    isPassage(question) ? { ...question, subQuestions: byPosition(question.subQuestions) } : question
  );
  const byId = new Map(questions.map((question) => [String(question?.id), question]));

  const positionOf = (id) =>
    position.has(id) ? position.get(id) : position.get(asArray(byId.get(id)?.subQuestions)[0]);

  return {
    sections: asArray(sections).map((section) => ({
      ...section,
      questions: asArray(section?.questions)
        .map(String)
        .filter((id) => positionOf(id) !== undefined)
        .sort((a, b) => positionOf(a) - positionOf(b)),
    })),
    assignmentQuestions: questions,
  };
};

/**
 * The number each question is shown with, keyed by id.
 *
 * Given the student's `responses`, numbering follows their rows: it is worked out at runtime
 * for display only, so on a randomised paper Q1 is the first thing this student is asked. The
 * paper's own order (assignment.sections) is untouched and still decides what the teacher, the
 * results and the downloads call a question. Without rows it numbers the paper as printed; on
 * an unshuffled paper the two agree.
 *
 * One number per thing the paper asks for. A passage takes one number and its sub-questions
 * hang off it — Q1, then Q2.1, Q2.2 for the block, then Q3. An "attempt any X of these Y"
 * rule takes ONE number for the whole block and letters its alternatives — 2a, 2b, 2c, with
 * the next question at 3 — because the alternatives are one question offered several ways.
 * The same applies inside a passage (2.2a, 2.2b, next sub-question 2.3), and a grouped
 * passage carries its letter down to its own rows (2a.1, 2a.2).
 *
 * A rule set to continuous numbering (its `numbering`) gives each of its alternatives a number
 * of its own instead - 2, 3, 4 with the next question at 5, and 2.2, 2.3 inside a passage -
 * still taken where the block starts, since that is where the paper prints them.
 *
 * This mirrors buildQuestionNumberLabels in assignment-fe
 * (src/components/app/canvas/redux/choice-group.js), which numbers the canvas and the
 * PDF/DOCX export. An unshuffled paper has to read the same on the screen a student answers it
 * on as on the paper they were handed, so the two must change together.
 */
export const buildDrawerQuestionNumbers = ({
  sections: paperSections = [],
  assignmentQuestions: paperQuestions = [],
  responses,
}) => {
  const { sections, assignmentQuestions } = asArray(responses).length
    ? orderByResponses({ sections: paperSections, assignmentQuestions: paperQuestions, responses })
    : { sections: paperSections, assignmentQuestions: paperQuestions };

  const isContinuous = (rule) => rule?.numbering === CHOICE_GROUP_NUMBERING.CONTINUOUS;
  /** An alternative's letter, or nothing when its rule numbers the alternatives in turn. */
  const memberSuffix = (rule, index) =>
    (isContinuous(rule) ? '' : GROUP_MEMBER_LETTERS[index] || '');

  const numbers = {};
  const byId = new Map(
    asArray(assignmentQuestions).map((question) => [String(question?.id), question])
  );

  const ruleByMember = new Map();
  asArray(sections).forEach((section) => {
    asArray(section?.choiceGroups).forEach((group) => {
      asArray(group?.questions).forEach((id) => ruleByMember.set(String(id), group));
    });
  });

  const labelled = new Set();

  /** A rule's members in the order they sit on the paper, so the letters follow the page. */
  const orderedMembers = (group, positionOf) =>
    asArray(group?.questions)
      .map(String)
      .filter((id) => positionOf.has(id))
      .sort((a, b) => positionOf.get(a) - positionOf.get(b));

  const labelSubQuestions = (parentId, parentLabel) => {
    const subIds = asArray(byId.get(parentId)?.subQuestions).map(String);
    const subPosition = new Map(subIds.map((id, index) => [id, index]));

    let subCounter = 0;
    subIds.forEach((subId) => {
      if (labelled.has(subId)) return;
      const rule = ruleByMember.get(subId);
      subCounter += 1;

      if (rule && String(rule.parentQuestionId) === String(parentId)) {
        orderedMembers(rule, subPosition).forEach((memberId, index) => {
          if (isContinuous(rule) && index > 0) subCounter += 1;
          numbers[memberId] = `${parentLabel}.${subCounter}${memberSuffix(rule, index)}`;
          labelled.add(memberId);
        });
        return;
      }

      numbers[subId] = `${parentLabel}.${subCounter}`;
      labelled.add(subId);
    });
  };

  const orderedIds = asArray(sections).flatMap((section) =>
    asArray(section?.questions).map(String)
  );
  const orderIndex = new Map(orderedIds.map((id, index) => [id, index]));

  let counter = 0;
  orderedIds.forEach((topLevelId) => {
    if (labelled.has(topLevelId)) return;
    const rule = ruleByMember.get(topLevelId);
    counter += 1;

    // A rule among a passage's own sub-questions is numbered with them, not out here.
    if (rule && !rule.parentQuestionId) {
      orderedMembers(rule, orderIndex).forEach((memberId, index) => {
        if (isContinuous(rule) && index > 0) counter += 1;
        const label = `${counter}${memberSuffix(rule, index)}`;
        numbers[memberId] = label;
        labelled.add(memberId);
        labelSubQuestions(memberId, label);
      });
      return;
    }

    numbers[topLevelId] = String(counter);
    labelled.add(topLevelId);
    labelSubQuestions(topLevelId, String(counter));
  });

  return numbers;
};

/** Every choice group on the paper, whichever section declared it. */
export const collectDrawerChoiceGroups = (sections = []) =>
  asArray(sections).flatMap((section) => asArray(section?.choiceGroups));
